import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CheckboxFormFieldComponent } from '../forms/fields/checkbox-form-field/checkbox-form-field.component';
import { DatepickerFormFieldComponent } from '../forms/fields/datepicker-form-field/datepicker-form-field.component';
import { InputFormFieldComponent } from '../forms/fields/input-form-field/input-form-field.component';
import { TextareaFormFieldComponent } from '../forms/fields/textarea-form-field/textarea-form-field.component';
import { DropdownEntryEnum, DropdownTypeEnum } from 'app/shared/enums/dropdowns.enum';
import { ConfigurationSelectFormFieldComponent } from '../forms/fields/configuration-select-form-field/configuration-select-form-field.component';
import { CaseFormService } from 'app/case-form/case-form.service';
import { SidebarService } from 'app/shell/sidebar/sidebar.service';
import { Subject, take, takeUntil } from 'rxjs';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';
import { ToastrService } from 'ngx-toastr';
import { IUser } from 'app/shared/models/users.model';
import { UsersService } from 'app/shared/services/users.service';
import { SelectFormFieldComponent } from '../forms/fields/select-form-field/select-form-field.component';
import { IEscalateCase } from 'app/shared/models/escalate-case.model';
import { HelperService } from 'app/shared/services/helper.service';
import { SelectFormEnum } from 'app/shared/enums/select-form.enum';

@Component({
  selector: 'app-escalate-case-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, CheckboxFormFieldComponent, DatepickerFormFieldComponent, ConfigurationSelectFormFieldComponent, SelectFormFieldComponent, InputFormFieldComponent, TextareaFormFieldComponent],
  templateUrl: './escalate-case-modal.component.html',
  styleUrl: './escalate-case-modal.component.scss'
})
export class EscalateCaseModalComponent {
  form!: FormGroup;
  dropdownType: string = DropdownTypeEnum.ESCALATION_CATEGORY;
  dropdownTypeEnum = DropdownTypeEnum;
  entry1 = DropdownEntryEnum.ENTRY_1;
  customerEscalated!: boolean;
  escalationTeamUsers: IUser[] = [];
  customerInternalUsers: IUser[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    public modalService: NgbActiveModal,
    private fb: FormBuilder,
    private caseFormService: CaseFormService,
    private sidebarService: SidebarService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private usersService: UsersService,
    private helperService: HelperService
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.getUsersData();
  }

  createForm() {
    this.form = this.fb.group({
      caseId: [null],
      isEscalated: [true],
      customerEscalated: [false],
      customerEscalatedDate: [{ value: null, disabled: true }],
      escalationCategory: [null],
      customerEscalatedByWhomInternal: [null],
      customerEscalatedByWhomExternal: [null],
      escalationTeam: [null],
      reasonForEscalation: [null]
    });

    if (this.sidebarService.ticketEscalationData && this.sidebarService.ticketEscalationId) {
      this.form.patchValue(this.sidebarService.ticketEscalationData);
    }
  }

  onCheckEscalateCustomer(value: boolean) {
    this.customerEscalated = value;
    const escalationCategory: FormControl = this.form.get('escalationCategory') as FormControl;
    const escalationTeam: FormControl = this.form.get('escalationTeam') as FormControl;
    const reasonForEscalation: FormControl = this.form.get('reasonForEscalation') as FormControl;
    const customerEscalatedDate: FormControl = this.form.get('customerEscalatedDate') as FormControl;

    if (value) {
      customerEscalatedDate?.patchValue(new Date());
      escalationCategory?.setValidators([Validators.required]);
      escalationTeam?.setValidators([Validators.required]);
      reasonForEscalation?.setValidators([Validators.required]);
    } else {
      customerEscalatedDate?.reset();
      escalationCategory?.clearValidators();
      escalationTeam?.clearValidators();
      reasonForEscalation?.clearValidators();
    }

    escalationCategory.updateValueAndValidity();
    escalationTeam.updateValueAndValidity();
    reasonForEscalation.updateValueAndValidity();
  }

  confirm() {
    this.form.get('isEscalated')?.setValue(true);
    const formValue: IEscalateCase = this.form.getRawValue();
    this.helperService.convertUsersToStrings(formValue, ['customerEscalatedByWhomInternal', 'escalationTeam'], false);
    this.caseFormService.escalateCase({ ...formValue, caseId: this.sidebarService.ticketEscalationId }).pipe(take(1)).subscribe(res => {
      this.toastr.show(
        this.translate.instant('messages.case_has_escalated_message'),
        ToastTypesEnum.SUCCESS
      );      
      this.sidebarService.ticketEscalationData = this.form.getRawValue();
      this.caseFormService.caseEscalated$.next(this.form.getRawValue());
      this.modalService.close();
      this.resetForm();
    });
  }

  onUserChange(data: any, formControlName: string) {
    if (data && data.length === 0) {
      this.form.get(formControlName)?.setValue(null);
    }
  }

  close() {
    this.modalService.dismiss();
    this.resetForm();
  }

  resetForm() {
    setTimeout(() => {
      this.form?.reset();
    }, 300);
  }

  getUsersData() {
    this.usersService.users$.subscribe((users: IUser[]) => {
      this.escalationTeamUsers = users;
      this.customerInternalUsers = users;
    });
  }

  searchUsers(event: string, term: string, usersList: string, page: number = 0, take: number) {
    this.usersService
    .getUsers(term, page * take, take)
    .pipe(takeUntil(this.destroy$))
    .subscribe((users: IUser[]) => {
      if (event === SelectFormEnum.SCROLL) {
        (this as any)[usersList] = [...(this as any)[usersList], ...users];
      } else if (event === SelectFormEnum.SEARCH) {
        (this as any)[usersList] = [...users];
      }
    });
  }
}
